import React, { useState, useEffect } from 'react';
import {
	Calendar,
	Edit3,
	Check,
	X,
	CheckSquare,
	Clock,
	MessageCircle,
} from 'lucide-react';
import Card from '../../../components/ui/card';
import Button from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';

const SchedulingCard = ({ client, clientDataHook }) => {
	const { updateCard, isCardEditMode, toggleCardEditMode, cancelCardEdit, isOnboarding, refreshData, isLoading } = clientDataHook;
	const cardName = 'scheduling';
	const isEditing = isOnboarding || isCardEditMode(cardName);
	
	// Refresh data when component mounts (for onboarding)
	useEffect(() => {
		if (isOnboarding && refreshData) {
			console.log('🟦 SchedulingCard - Component mounted, refreshing data');
			refreshData();
		}
	}, [isOnboarding]);

	// Form state for onboarding mode
	const [formValues, setFormValues] = useState({
		faqAcknowledged: client?.faqAcknowledged || false,
		preferredMeetingTimes: client?.preferredMeetingTimes || '',
		additionalNotes: client?.additionalNotes || ''
	});

	// Update form values when client data changes
	useEffect(() => {
		setFormValues({
			faqAcknowledged: client?.faqAcknowledged || false,
			preferredMeetingTimes: client?.preferredMeetingTimes || '',
			additionalNotes: client?.additionalNotes || ''
		});
	}, [client]);

	const fields = [
		{
			key: 'faqAcknowledged',
			label: 'Acknowledgment of FAQ',
			type: 'checkbox',
			value: client?.faqAcknowledged || false,
			icon: CheckSquare,
			placeholder: 'Client has acknowledged reading the FAQ',
		},
		{
			key: 'preferredMeetingTimes',
			label: 'Preferred days & times to meet virtually',
			type: 'textarea',
			value: client?.preferredMeetingTimes || '',
			icon: Clock,
			placeholder: 'e.g., Weekdays 9-11 AM PST, avoid Fridays after 2 PM',
		},
		{
			key: 'additionalNotes',
			label: 'Anything else we need to know?',
			type: 'textarea',
			value: client?.additionalNotes || '',
			icon: MessageCircle,
			placeholder: 'Any additional information, special requirements, or notes...',
		},
	];

	
	// Handle form field changes for onboarding
	const handleFormFieldChange = (fieldKey, value) => {
		setFormValues(prev => ({
			...prev,
			[fieldKey]: value
		}));
	};
	
	// Handle save for onboarding mode
	const handleOnboardingSave = async () => {
		const success = await updateCard(formValues);
		return success;
	};

	// Handle save for profile mode (card-wide editing)
	const handleSave = async () => {
		const success = await updateCard(formValues);
		if (success) {
			toggleCardEditMode(cardName); // Exit edit mode
		}
	};

	// Handle cancel for profile mode (card-wide editing)
	const handleCancel = () => {
		// Reset form values to original client data
		setFormValues({
			faqAcknowledged: client?.faqAcknowledged || false,
			preferredMeetingTimes: client?.preferredMeetingTimes || '',
			additionalNotes: client?.additionalNotes || ''
		});
		cancelCardEdit(cardName);
	};

	return (
		<Card>
			{!isOnboarding && (
				<Card.Header className='flex flex-row items-center justify-center space-y-0 pb-4'>
					<div className='space-y-1 text-center'>
						<Card.Title className='flex items-center gap-2 text-lg font-montserrat-medium text-gray-900 justify-center'>
							<Calendar className='h-5 w-5' />
							Scheduling & Notes
						</Card.Title>
						<Card.Description className='text-sm text-gray-600'>
							Your meeting preferences and additional information.
						</Card.Description>
					</div>
					{!isEditing && (
						<Button
							size='sm'
							variant='outline'
							onClick={() => toggleCardEditMode(cardName)}
							className='border-gray-300 text-gray-600 hover:bg-gray-50 ml-4'>
							<Edit3 className='h-4 w-4 mr-2' />
							Edit
						</Button>
					)}
				</Card.Header>
			)}
			<Card.Content className='space-y-4'>
				{fields.map((field) => (
					<div key={field.key} className='space-y-2'>
						<label className='text-sm font-semibold text-gray-800'>
							{field.label}
						</label>
						
						{isEditing ? (
							<div className='space-y-2'>
								{field.type === 'checkbox' ? (
									<div className='flex items-center gap-2'>
										<input
											type='checkbox'
											checked={formValues[field.key] || false}
											onChange={(e) => handleFormFieldChange(field.key, e.target.checked)}
											className='w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-500'
										/>
										<span className='text-sm text-gray-700'>{field.placeholder}</span>
									</div>
								) : (
									<Textarea
										value={formValues[field.key] || ''}
										onChange={(e) => handleFormFieldChange(field.key, e.target.value)}
										placeholder={field.placeholder}
										className='w-full min-h-[80px]'
									/>
								)}
							</div>
						) : field.type === 'checkbox' ? (
							<div className='flex items-start gap-2'>
								<CheckSquare className='h-4 w-4 text-gray-500 mt-0.5' />
								<div className='flex-1'>
									<span className='text-sm font-montserrat-regular text-gray-900'>
										{field.value ? 'Acknowledged' : 'Not acknowledged'}
									</span>
								</div>
							</div>
						) : (
							<div className='flex items-start gap-2'>
								<field.icon className='h-4 w-4 text-gray-500 mt-0.5' />
								<div className='flex-1'>
									<span className='text-sm font-montserrat-regular text-gray-900 whitespace-pre-wrap'>
										{field.value || (
											<span className='text-gray-400 italic'>
												{field.placeholder}
											</span>
										)}
									</span>
								</div>
							</div>
						)}
					</div>
				))}
				
				{/* Save/Cancel buttons when editing in profile mode */}
				{isEditing && !isOnboarding && (
					<div className='flex justify-end gap-3 pt-4 border-t border-gray-200'>
						<Button
							variant='outline'
							onClick={handleCancel}
							className='border-gray-300 text-gray-600 hover:bg-gray-50'>
							<X className='h-4 w-4 mr-2' />
							Cancel
						</Button>
						<Button
							onClick={handleSave}
							className='bg-gray-900 hover:bg-gray-800 text-white'>
							<Check className='h-4 w-4 mr-2' />
							Save Changes
						</Button>
					</div>
				)}
				
				{/* Save button for onboarding */}
				{isOnboarding && (
					<div className='pt-6 mt-6 border-t border-gray-200'>
						<Button
							onClick={handleOnboardingSave}
							className='w-full bg-gray-900 hover:bg-gray-800 text-white'>
							<Check className='h-4 w-4 mr-2' />
							Save & Continue
						</Button>
					</div>
				)}
			</Card.Content>
		</Card>
	);
};

export default SchedulingCard;